import { algoliasearch } from "algoliasearch";
import type { SearchClient, SearchResponse } from "algoliasearch";
import { fetchProductSearchConfig } from "../api/fetchProductSearchConfig";
import { ProductSearchConfig } from "../api/fetchProductSearchConfig/types";
import { ProductStoreProduct } from "../api/fetchProductStoreProducts/types";

interface AlgoliaProductHit {
  objectID: string;
  productId: number;
  uuid: string;
  code: string;
  name: string;
  description: string;
  price: number;
  type: "physical" | "virtual";
  categoryName: string;
  categoryColor: string | null;
  isActive: boolean;
  isPublished: boolean;
  viewCount: number;
  thumbnail: string | null;
  thumbnailFileUuid: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface ProductSearchFilters {
  categoryName?: string;
  type?: string;
}

export interface ProductSearchResult {
  hits: ProductStoreProduct[];
  totalCount: number;
  facets: {
    categories: Record<string, number>;
    types: Record<string, number>;
  };
}

class ProductDirectoryAlgoliaService {
  private searchClient: SearchClient | null = null;
  private config: ProductSearchConfig | null = null;
  private configPromise: Promise<ProductSearchConfig> | null = null;

  async initialize(forceRefresh: boolean = false): Promise<void> {
    if (forceRefresh || !this.configPromise) {
      this.configPromise = fetchProductSearchConfig();
    }

    this.config = await this.configPromise;

    this.searchClient = algoliasearch(
      this.config.applicationId,
      this.config.securedApiKey,
    );
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.searchClient || !this.config) {
      await this.initialize();
    }

    if (this.config && this.config.expiresAt < Date.now() / 1000) {
      await this.initialize(true);
    }
  }

  async searchProducts(
    query: string,
    filters: ProductSearchFilters,
    page: number,
    hitsPerPage: number = 20,
  ): Promise<ProductSearchResult> {
    await this.ensureInitialized();

    if (!this.searchClient || !this.config) {
      throw new Error("Search client not initialized");
    }

    const algoliaFilters: string[] = [];

    if (filters.categoryName) {
      algoliaFilters.push(`categoryName:"${filters.categoryName}"`);
    }

    if (filters.type) {
      algoliaFilters.push(`type:"${filters.type}"`);
    }

    const searchResponse = await this.searchClient.search<AlgoliaProductHit>({
      requests: [
        {
          indexName: this.config.index,
          query,
          filters:
            algoliaFilters.length > 0 ? algoliaFilters.join(" AND ") : "",
          facets: ["categoryName", "type"],
          hitsPerPage,
          page,
        },
      ],
    });

    const result = searchResponse
      .results[0] as SearchResponse<AlgoliaProductHit>;

    const hits: ProductStoreProduct[] = (result.hits || []).map(
      transformHitToProduct,
    );

    return {
      hits,
      totalCount: result.nbHits || 0,
      facets: {
        categories:
          (result.facets?.categoryName as Record<string, number>) || {},
        types: (result.facets?.type as Record<string, number>) || {},
      },
    };
  }

  clearCache(): void {
    this.searchClient = null;
    this.config = null;
    this.configPromise = null;
  }
}

function transformHitToProduct(hit: AlgoliaProductHit): ProductStoreProduct {
  return {
    id: hit.uuid,
    code: hit.code,
    name: hit.name,
    description: hit.description,
    price: String(hit.price),
    type: hit.type,
    category: {
      id: "",
      name: hit.categoryName,
      color: hit.categoryColor ? { id: 0, value: hit.categoryColor } : null,
    },
    isActive: hit.isActive,
    isPublished: hit.isPublished,
    viewCount: hit.viewCount,
    thumbnailUrl: hit.thumbnail,
    thumbnailFileUuid: hit.thumbnailFileUuid ?? null,
    createdAt: new Date(hit.createdAt * 1000).toISOString(),
    updatedAt: new Date(hit.updatedAt * 1000).toISOString(),
  };
}

export const productDirectoryAlgoliaService =
  new ProductDirectoryAlgoliaService();
